import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

// Components
import Login from "@/components/Login";
import AdminDashboard from "@/components/AdminDashboard";
import CollaborateurDashboard from "@/components/CollaborateurDashboard";
import CollaborateurDossiersView from "@/components/CollaborateurDossiersView";
import VehicleForm from "@/components/VehicleForm";
import VehicleDetail from "@/components/VehicleDetail";
import UsersManagement from "@/components/UsersManagement";
import WorkflowConfig from "@/components/WorkflowConfig";
import PrestationsConfig from "@/components/PrestationsConfig";
import KanbanView from "@/components/KanbanView";
import TodayView from "@/components/TodayView";
import DossiersView from "@/components/DossiersView";
import ClientsView from "@/components/ClientsView";
import VehiculesDBView from "@/components/VehiculesDBView";
import SettingsView from "@/components/SettingsView";
import RentabiliteView from "@/components/RentabiliteView";
import AuBureauView from "@/components/AuBureauView";
import SmsCenter from "@/components/SmsCenter";
import PiecesView from "@/components/PiecesView";
import Sidebar from "@/components/Sidebar";
import AlertsPanel from "@/components/AlertsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const APP_NAME = "Carrosserie Auto Gardoise";

// Contexts
const AuthContext = createContext(null);
const SidebarContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useSidebar = () => useContext(SidebarContext);

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Logout function - declared first as it's used by other functions
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setPermissions(null);
  };

  // Fetch user permissions from API
  const fetchPermissions = async () => {
    try {
      const response = await api.get("/auth/me");
      const userData = response.data;
      setUser(userData);
      setPermissions(userData.permissions || { menus: [], actions: [] });
      localStorage.setItem("user", JSON.stringify(userData));
      return userData;
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      logout();
      return null;
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (storedUser && token) {
      // Refresh permissions from server
      fetchPermissions().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username, password) => {
    try {
      const response = await api.post("/auth/login", { username, password });
      const { token, user: userData } = response.data;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      
      // Fetch full permissions after login
      await fetchPermissions();
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || "Erreur" };
    }
  };

  // Helper functions to check permissions
  const hasMenuAccess = (menuKey) => {
    if (!permissions) return false;
    // Admin always has access
    if (user?.role === "admin") return true;
    return permissions.menus?.includes(menuKey) || false;
  };

  const hasActionAccess = (actionKey) => {
    if (!permissions) return false;
    // Admin always has access
    if (user?.role === "admin") return true;
    return permissions.actions?.includes(actionKey) || false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, permissions, login, logout, hasMenuAccess, hasActionAccess }}>
      {children}
    </AuthContext.Provider>
  );
}

function SidebarProvider({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = () => setCollapsed(!collapsed);
  
  return (
    <SidebarContext.Provider value={{ collapsed, toggle }}>
      {children}
    </SidebarContext.Provider>
  );
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin" && user.specialite !== "secretaire") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppLayout({ children }) {
  const { user } = useAuth();
  const { collapsed } = useSidebar();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  if (!user) return children;
  
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className={`flex-1 transition-all duration-300 ${
        isMobile ? 'mt-14 ml-0' : (collapsed ? 'ml-16' : 'ml-64')
      }`}>
        {children}
      </main>
      {!isMobile && (user.role === "admin" || user.specialite === "secretaire") && <AlertsPanel />}
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.specialite === "secretaire";
  
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout>
            {isAdmin ? <AdminDashboard /> : <CollaborateurDashboard />}
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/kanban" element={
        <ProtectedRoute>
          <AppLayout><KanbanView /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/dossiers" element={
        <ProtectedRoute>
          <AppLayout>
            {isAdmin ? <DossiersView /> : <CollaborateurDossiersView />}
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/today" element={
        <ProtectedRoute>
          <AppLayout><TodayView /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/rentabilite" element={
        <ProtectedRoute adminOnly>
          <AppLayout><RentabiliteView /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/au-bureau" element={
        <ProtectedRoute adminOnly>
          <AppLayout><AuBureauView /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/vehicles/new" element={
        <ProtectedRoute adminOnly>
          <AppLayout><VehicleForm /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/vehicles/:id" element={
        <ProtectedRoute>
          <AppLayout><VehicleDetail /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/vehicles/:id/edit" element={
        <ProtectedRoute adminOnly>
          <AppLayout><VehicleForm edit /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/users" element={
        <ProtectedRoute adminOnly>
          <AppLayout><UsersManagement /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/workflow" element={
        <ProtectedRoute adminOnly>
          <AppLayout><WorkflowConfig /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/prestations" element={
        <ProtectedRoute adminOnly>
          <AppLayout><PrestationsConfig /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/clients" element={
        <ProtectedRoute adminOnly>
          <AppLayout><ClientsView /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/vehicules-db" element={
        <ProtectedRoute adminOnly>
          <AppLayout><VehiculesDBView /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute adminOnly>
          <AppLayout><SettingsView /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/sms" element={
        <ProtectedRoute adminOnly>
          <AppLayout><SmsCenter /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/pieces" element={
        <ProtectedRoute adminOnly>
          <AppLayout><PiecesView /></AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SidebarProvider>
          <Toaster position="top-right" richColors />
          <AppRoutes />
        </SidebarProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
